// SF-029 Hot Works Permit - PDF layout.
//
// Extracted verbatim from HotWorksPermitPage.js so the dashboard and the
// auto-sync worker render byte-identical documents. The only changes are the
// logo (now a baked asset instead of `new Image()` + canvas) and the signature
// cells (now the shared drawImageInCell helper).
//
// This module must stay free of browser APIs.

import { jsPDF, autoTable } from "../pdfLib.js";
import {
  drawHeaderLogo,
  drawImageInCell,
  applyFooterAllPages,
  buildSharePointFileName,
  toDdMmYyyy,
} from "../helpers.js";

export const meta = {
  slug: "hot-works-permit",
  formName: "SF-029 Hot Works Permit",
  uploadContext: "hseq-forms-tablets",
  uploadSubFolder: "SF-029 Hot Works Permit",
};

// Also rendered by the form UI in HotWorksPermitPage - keep one definition.
export const COMPLETION_STATEMENT =
  "This confirms that the immediate work area and all adjacent areas to which sparks and heat might have spread (such as floors below and above and areas on other sides of walls) have been inspected and found to be free of fire following work completion one hour after hot works completed and any fire protection and alarm systems reinstated.";
export const PERMIT_NOTE =
  "Note: HOT WORK permits are NOT to be issued for long periods. A new permit must be issued where work extends for MORE than ONE day";

export const PRECAUTION_GROUPS = [
  {
    title: "1. Fire Protection",
    items: [
      { key: "waterSprinklersIsolated", label: "Water sprinklers/heat detectors isolated?" },
      { key: "fixedFireExtinguisherIsolated", label: "Fixed Fire Extinguisher system isolated?" },
      { key: "portableExtinguisherAvailable", label: "Portable extinguisher immediately available?" },
      { key: "meansOfEscape", label: "Means of escape and method of raising the alarm identified & communicated?" },
      { key: "personAppointedFireWatch", label: "Person appointed for fire watch duties (add name)?" },
    ],
  },
  {
    title: "2. Precautions within HOT WORKING area",
    items: [
      { key: "accessRestricted", label: "Access to Area Restricted - barriers and warning signs erected?" },
      { key: "sufficientVentilation", label: "Sufficient ventilation? If not forced ventilation installed and gas monitoring in place." },
      { key: "combustibleMaterialsRemoved", label: "Combustible materials removed from area/ adjacent area or covered with non-combustible shield?" },
      { key: "flammableLiquidsRemoved", label: "Flammable liquids & gases removed from the area?" },
    ],
  },
  {
    title: "3. HOT WORK Equipment and PPE/RPE",
    items: [
      { key: "weldingEquipmentInspected", label: "Welding equipment inspected & in date?" },
      { key: "cylindersSecured", label: "Cylinders secured in a vertical position, fitted with correct regulator, flashback arrestor and valve key?" },
      { key: "ppeInUse", label: "Gloves/gauntlets, goggles/welders visor, FR coveralls, welding fume respirator in use?" },
    ],
  },
];

/** Builds the document. Returns a jsPDF instance; the caller decides what to do with it. */
export async function buildPdf(record) {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const primaryColor = [139, 22, 52];
    const headerHeight = 30;
    doc.setFillColor(...primaryColor);
    doc.rect(0, 0, pageWidth, headerHeight, "F");

    drawHeaderLogo(doc, { headerHeight });


    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.setTextColor(255, 255, 255);
    doc.text("SF-029 Hot Works Permit", 90, 15);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text("TAYLOR TECHNOLOGY SYSTEMS LIMITED", 90, 20.5);

    const innerW = pageWidth - 20;
    const labelCell = (t) => ({ content: t, styles: { fillColor: primaryColor, textColor: [255, 255, 255], fontStyle: "bold" } });
    const val = (t) => ({ content: t || "", styles: { fontStyle: "bold" } });
    const groupRow = (t) => [{ content: t, colSpan: 3, styles: { fillColor: [239, 226, 231], textColor: [139, 22, 52], fontStyle: "bold" } }];
    const common = {
      styles: { font: "helvetica", fontSize: 9, cellPadding: 2.5, overflow: "linebreak", valign: "middle", lineColor: [203, 213, 225], lineWidth: 0.2 },
      bodyStyles: { fontStyle: "bold" }, // all values shown in bold in the PDF
      theme: "grid",
      tableWidth: innerW,
      margin: { left: 10, right: 10 },
    };
    const p = (key, field) => (record.precautions && record.precautions[key] && record.precautions[key][field]) || "";

    // A. Preparation header
    autoTable(doc, {
      ...common,
      startY: headerHeight + 8,
      body: [
        [{ content: "A. HOT WORK PREPARATION — To be completed by competent operative or person responsible", colSpan: 2, styles: { fillColor: primaryColor, textColor: [255, 255, 255], fontStyle: "bold" } }],
        [labelCell("Site/Substation Name:"), val(record.siteName)],
        [labelCell("Location of hot work:"), val(record.locationOfHotWork)],
        [labelCell("Nature of HOT WORK (ARC, Oxy-fuel, Grinding etc):"), val(record.natureOfHotWork)],
      ],
      columnStyles: { 0: { cellWidth: 70 } },
    });

    // Precautions table
    const precBody = [
      [
        { content: "Safe Systems – Precautionary Actions", styles: { fillColor: primaryColor, textColor: [255, 255, 255], fontStyle: "bold" } },
        { content: "Yes/No/N/A", styles: { fillColor: primaryColor, textColor: [255, 255, 255], fontStyle: "bold", halign: "center" } },
        { content: "Specific Controls Put in Place", styles: { fillColor: primaryColor, textColor: [255, 255, 255], fontStyle: "bold" } },
      ],
    ];
    PRECAUTION_GROUPS.forEach((g) => {
      precBody.push(groupRow(g.title));
      g.items.forEach((it) => {
        precBody.push([{ content: it.label }, { content: p(it.key, "answer"), styles: { halign: "center", fontStyle: "bold" } }, { content: p(it.key, "controls"), styles: { fontStyle: "bold" } }]);
      });
    });
    autoTable(doc, { ...common, startY: doc.lastAutoTable.finalY, body: precBody, columnStyles: { 0: { cellWidth: 95 }, 1: { cellWidth: 25 } } });

    // A signoff
    autoTable(doc, {
      ...common,
      startY: doc.lastAutoTable.finalY,
      body: [
        [labelCell("Signed:"), { content: "", styles: { minCellHeight: 18 } }, labelCell("Name:"), val(record.preparedName)],
        [labelCell("Date:"), val(record.preparedDate), labelCell("Position:"), val(record.preparedPosition)],
      ],
      columnStyles: { 0: { cellWidth: 28 }, 2: { cellWidth: 28 } },
      didDrawCell: (d) => {
        if (d.section === "body" && d.row.index === 0 && d.column.index === 1) {
          drawImageInCell(doc, d, record.preparedSigned);
        }
      },
    });

    // B. Authorisation
    autoTable(doc, {
      ...common,
      startY: doc.lastAutoTable.finalY + 3,
      body: [
        [{ content: "B. HOT WORK AUTHORISATION to Commence — To be completed by the Principal Contractor or Authorised Person", colSpan: 4, styles: { fillColor: primaryColor, textColor: [255, 255, 255], fontStyle: "bold" } }],
        [{ content: `This permit is issued to ${record.issuedTo || "________"} subject to the controls above being put in place and a fire watch being completed.`, colSpan: 4 }],
        [labelCell("Time of issue:"), val(record.timeOfIssue), labelCell("Time of expiry:"), val(record.timeOfExpiry)],
        [labelCell("Signed:"), { content: "", styles: { minCellHeight: 18 } }, labelCell("Name:"), val(record.authName)],
        [labelCell("Date:"), val(record.authDate), labelCell("Position:"), val(record.authPosition)],
      ],
      columnStyles: { 0: { cellWidth: 28 }, 2: { cellWidth: 28 } },
      didDrawCell: (d) => {
        if (d.section === "body" && d.row.index === 3 && d.column.index === 1) {
          drawImageInCell(doc, d, record.authSigned);
        }
      },
    });

    // C. Completion
    autoTable(doc, {
      ...common,
      startY: doc.lastAutoTable.finalY + 3,
      body: [
        [{ content: "C. HOT WORK COMPLETION and FIRE WATCH — To be completed by a trained welding operator or contractor", colSpan: 4, styles: { fillColor: primaryColor, textColor: [255, 255, 255], fontStyle: "bold" } }],
        [{ content: COMPLETION_STATEMENT, colSpan: 4, styles: { fontSize: 8 } }],
        [{ content: "Time Area Inspection & Fire Watch Completed:", colSpan: 2, styles: { fillColor: [248, 241, 244], textColor: [139, 22, 52], fontStyle: "bold" } }, { content: record.timeAreaInspectionCompleted || "", colSpan: 2, styles: { fontStyle: "bold" } }],
        [labelCell("Signed:"), { content: "", styles: { minCellHeight: 18 } }, labelCell("Name:"), val(record.completionName)],
        [labelCell("Date:"), val(record.completionDate), labelCell("Position:"), val(record.completionPosition)],
        [{ content: "Permit Cancelled by (Principal Contractor or Authorised Person):", colSpan: 4, styles: { fillColor: [248, 241, 244], textColor: [139, 22, 52], fontStyle: "bold" } }],
        [labelCell("Signed:"), { content: "", styles: { minCellHeight: 18 } }, labelCell("Name:"), val(record.cancelledName)],
        [labelCell("Date:"), val(record.cancelledDate), labelCell("Position:"), val(record.cancelledPosition)],
        [{ content: PERMIT_NOTE, colSpan: 4, styles: { fontSize: 8, fontStyle: "bolditalic" } }],
      ],
      columnStyles: { 0: { cellWidth: 28 }, 2: { cellWidth: 28 } },
      didDrawCell: (d) => {
        if (d.section !== "body" || d.column.index !== 1) return;
        if (d.row.index === 3) drawImageInCell(doc, d, record.completionSigned);
        else if (d.row.index === 6) drawImageInCell(doc, d, record.cancelledSigned);
      },
    });
  applyFooterAllPages(doc);

  return doc;
}

/** "PICOS NAME - SITE - DATE ISSUED.pdf" for SharePoint, or a local download name. */
export function buildFileName(record, { forUpload = false } = {}) {
  if (forUpload) {
    return buildSharePointFileName([
      record.preparedName,
      record.siteName || record.locationOfHotWork,
      toDdMmYyyy(record.preparedDate),
    ]);
  }
  const safeName = String(record.siteName || record.locationOfHotWork || "hot-works-permit").replace(
    /[\\/:"*?<>|]+/g,
    "-"
  );
  return `SF029_${safeName}.pdf`;
}
